<template>
    <section class="education-section card">
        <div class="section-header">
            <h2>{{ $trans("web_profile.education") }}</h2>
            <EditButton :onClick="goToEducation" />
        </div>

        <div v-loading="loading" class="min-h-[200px]">
            <EasyDataTable
                v-if="!loading && localEducation.length"
                :headers="educationHeaders"
                :items="localEducation"
                table-class-name="custom-education-table"
                hide-footer
                :border-cell="false"
            >
                <template #empty-message>
                    <div style="text-align: center; padding: 20px; color: #888">
                        {{ $trans("web_profile.no_education_found") }}
                    </div>
                </template>

                <template #header-period>
                    <div class="header-multiline">
                        <span>{{ $trans("web_profile.from_to") }}</span>
                        <span>{{ $trans("web_profile.duration") }}</span>
                    </div>
                </template>

                <template #header-description>
                    <div class="header-single">
                        <span>{{ $trans("web_profile.description") }}</span>
                    </div>
                </template>

                <template #header-degree>
                    <div class="header-single">
                        <span>{{ $trans("web_profile.education_level") }}</span>
                    </div>
                </template>

                <template #item-period="{ from, to, duration }">
                    <div class="cell-multiline">
                        <div class="font-bold">
                            {{ formatDate(from) }} - {{ formatDate(to) }}
                        </div>
                        <div class="text-sm" v-if="duration">
                            {{ duration }}
                        </div>
                    </div>
                </template>

                <template #item-description="{ description }">
                    <div class="font-bold">
                        {{ description || "-" }}
                    </div>
                </template>

                <template #item-degree="{ education_level }">
                    <div class="font-bold">
                        {{ education_level || "-" }}
                    </div>
                </template>
            </EasyDataTable>
        </div>
    </section>
</template>

<script>
import { mapStores } from "pinia";
import { useWebProfileStore } from "@/stores/WebProfileStore";
import EditButton from "./../Components/EditButton.vue";
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";

export default {
    name: "EducationSection",
    components: { EasyDataTable, EditButton },
    data() {
        return {
            loading: true,
            localEducation: [],
            educationHeaders: [
                { text: "", value: "period", width: 200 },
                { text: "", value: "description" },
                { text: "", value: "degree" },
            ],
        };
    },
    computed: {
        ...mapStores(useWebProfileStore),
    },
    watch: {
        "WebProfileStore.education": {
            handler(newEducation) {
                if (!newEducation) return;
                this.mapEducation(newEducation);
            },
            immediate: true,
        },
    },
    methods: {
        mapEducation(rawEducation) {
            let processedEducation = [...rawEducation];
            processedEducation.sort((a, b) => {
                const dateA = a.to ? new Date(a.to) : new Date();
                const dateB = b.to ? new Date(b.to) : new Date();
                return dateB - dateA;
            });

            this.localEducation = processedEducation.map((edu) => ({
                id: edu.id,
                from: edu.from,
                to: edu.to,
                duration: edu.duration,
                description: edu.description,
                education_level: edu.education_level,
            }));
            this.loading = false;
        },
        formatDate(dateString) {
            if (!dateString) return this.$trans("web_profile.present");
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return `${String(date.getMonth() + 1).padStart(
                2,
                "0"
            )}/${date.getFullYear()}`;
        },
        goToEducation() {
            const id = this.WebProfileStore.profile.id;
            if (!id) return;
            window.open(`/candidates/${id}/education`, '_blank');
        },
    },
};
</script>

<style scoped>
.education-section {
    padding: 1rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: #fff;
}

.cell-multiline,
.header-multiline {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.cell-multiline {
    padding: 8px 0;
}

.cell-single,
.header-single {
    display: flex;
    align-items: center;
    height: 100%;
    padding: 8px 0;
}

:deep(.custom-education-table) {
    --easy-table-border: none;
    --easy-table-row-border: none;
    --easy-table-header-font-size: 13px;
    --easy-table-body-row-font-size: 13px;
    --easy-table-header-background-color: transparent;
    --easy-table-body-row-background-color: transparent;
    --easy-table-body-row-font-color: #000;
}

:deep(.vue3-easy-data-table__main),
:deep(.vue3-easy-data-table__body tr) {
    background-color: transparent !important;
}

:deep(.vue3-easy-data-table__main tr td) {
    background-color: #eaf3f7 !important;
    border: 4.5px solid #fff !important;
    border-radius: 4px;
    font-weight: 700 !important;
    color: #000 !important;
    font-size: 16px !important;
}

:deep(.vue3-easy-data-table__main tr th) {
    background-color: transparent !important;
    border: none !important;
    pointer-events: none;
    padding-left: 10px;
    vertical-align: bottom;
    padding-bottom: 10px;
    font-weight: 700 !important;
    color: #000 !important;
    font-size: 16px !important;
}

:deep(.vue3-easy-data-table__body tr:hover td) {
    background-color: #eaf3f7 !important;
}

:deep(.vue3-easy-data-table__main) {
    overflow-y: hidden !important;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

h3 {
    margin: 0;
    font-size: 1.875rem;
    font-weight: 600;
}
</style>
